import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Box } from '@chakra-ui/react';
import {
  ComposableMap,
  Geographies,
  Geography,
  Marker,
} from 'react-simple-maps';

interface MapLocation {
  name: string;
  'Map Location'?: string;
  Type?: 'HQ' | 'Presence';
}

interface WorldLocationsMapProps {
  locations: MapLocation[];
}

interface ProcessedLocation {
  name: string;
  coordinates: [number, number];
  type: 'HQ' | 'Presence' | undefined;
  offsetX: number;
  offsetY: number;
}

const GEO_URL =
  'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json';

const MAP_WIDTH = 800;
const MAP_HEIGHT = 400;

// Geographic distance at which markers are considered close.
const CLOSE_LOCATION_THRESHOLD = 5;

// Desired separation between close markers in screen pixels.
const MARKER_OFFSET = -8;

// Desired marker diameter in screen pixels.
const MARKER_SIZE = 25;

const parseMapLocation = (
  value?: string
): [number, number] | null => {
  if (!value) {
    return null;
  }

  const parts = value.split(',');

  if (parts.length !== 2) {
    return null;
  }

  const latitude = Number(parts[0].trim());
  const longitude = Number(parts[1].trim());

  if (
    Number.isNaN(latitude) ||
    Number.isNaN(longitude)
  ) {
    return null;
  }

  // react-simple-maps requires:
  // [longitude, latitude]
  return [longitude, latitude];
};

const WorldLocationsMap = ({
  locations,
}: WorldLocationsMapProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] =
    useState(MAP_WIDTH);

  useEffect(() => {
    const element = containerRef.current;

    if (!element) {
      return;
    }

    const updateWidth = () => {
      setContainerWidth(element.getBoundingClientRect().width);
    };

    updateWidth();

    const resizeObserver = new ResizeObserver(() => {
      updateWidth();
    });

    resizeObserver.observe(element);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const mapScale =
    containerWidth > 0
      ? containerWidth / MAP_WIDTH
      : 1;

  /*
   * SVG user units grow/shrink with the responsive map.
   * Dividing by mapScale keeps these values visually
   * consistent in actual screen pixels.
   */
  const responsiveMarkerSize =
  containerWidth < 768 ? 12 : MARKER_SIZE;

  const responsiveStrokeWidth =
  containerWidth < 480
    ? 1.5
    : containerWidth < 768
      ? 1
      : 0.5;

  const markerSizeInSvgUnits =
  responsiveMarkerSize / mapScale;

  const markerOffsetInSvgUnits =
    MARKER_OFFSET / mapScale;

  const processedLocations =
    useMemo<ProcessedLocation[]>(() => {
      const validLocations: ProcessedLocation[] = [];

      locations.forEach((location) => {
        const coordinates = parseMapLocation(
          location['Map Location']
        );

        if (!coordinates) {
          return;
        }

        validLocations.push({
          name: location.name,
          coordinates,
          type: location.Type,
          offsetX: 0,
          offsetY: 0,
        });
      });

      const processed = new Set<number>();

      validLocations.forEach((location, index) => {
        if (processed.has(index)) {
          return;
        }

        const clusterIndexes = validLocations
          .map((otherLocation, otherIndex) => {
            const longitudeDifference = Math.abs(
              location.coordinates[0] -
                otherLocation.coordinates[0]
            );

            const latitudeDifference = Math.abs(
              location.coordinates[1] -
                otherLocation.coordinates[1]
            );

            const isClose =
              longitudeDifference <
                CLOSE_LOCATION_THRESHOLD &&
              latitudeDifference <
                CLOSE_LOCATION_THRESHOLD;

            return isClose ? otherIndex : -1;
          })
          .filter((otherIndex) => otherIndex !== -1);

        if (clusterIndexes.length <= 1) {
          processed.add(index);

          return;
        }

        clusterIndexes.forEach(
          (clusterIndex, position) => {
            const angle =
              (position / clusterIndexes.length) *
              Math.PI *
              2;

            /*
             * Store the direction here.
             * The actual pixel offset is applied when rendering.
             */
            validLocations[clusterIndex].offsetX =
              Math.cos(angle);

            validLocations[clusterIndex].offsetY =
              Math.sin(angle);

            processed.add(clusterIndex);
          }
        );
      });

      return validLocations;
    }, [locations]);

  return (
    <Box
      ref={containerRef}
      width="100%"
    >
      <ComposableMap
        projection="geoEquirectangular"
        projectionConfig={{
          scale: 147,
        }}
        width={MAP_WIDTH}
        height={MAP_HEIGHT}
        style={{
          width: '100%',
          height: 'auto',
          display: 'block',
        }}
      >
        <Geographies geography={GEO_URL}>
          {({ geographies }) =>
            geographies.map((geo) => (
              <Geography
  key={geo.rsmKey}
  geography={geo}
  fill="#FFFFFF"
  stroke="#898989"
  strokeWidth={responsiveStrokeWidth}
  style={{
    default: {
      outline: 'none',
    },
    hover: {
      outline: 'none',
    },
    pressed: {
      outline: 'none',
    },
  }}
/>
            ))
          }
        </Geographies>

        {processedLocations.map(
          (location, index) => {
            const markerColor =
              location.type === 'HQ'
                ? '#3BA6D8'
                : '#F7941D';

            const offsetX =
              location.offsetX *
              markerOffsetInSvgUnits;

            const offsetY =
              location.offsetY *
              markerOffsetInSvgUnits;

            return (
              <Marker
                key={`${location.name}-${index}`}
                coordinates={location.coordinates}
              >
                <g
                  transform={`translate(${offsetX}, ${offsetY})`}
                >
                  <foreignObject
                    x={-markerSizeInSvgUnits / 2}
                    y={-markerSizeInSvgUnits / 2}
                    width={markerSizeInSvgUnits}
                    height={markerSizeInSvgUnits}
                    style={{
                      overflow: 'visible',
                    }}
                  >
                    <div
                      title={location.name}
                      style={{
                        width: '100%',
                        height: '100%',
                        borderRadius: '50%',
                        backgroundColor: markerColor,
                        border: '2px solid white',
                        boxSizing: 'border-box',
                        cursor: 'pointer',
                      }}
                    />
                  </foreignObject>
                </g>
              </Marker>
            );
          }
        )}
      </ComposableMap>
    </Box>
  );
};

export default WorldLocationsMap;